import React, { useCallback, useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { action } from 'mobx';
import moment from 'moment';
import Modal from 'react-modal';
import LoadingSpinner from 'ui/components/LoadingSpinner';
import withCustomComponent from 'ui/components/withCustomComponent';

import BookingResourceSelector from 'ui/_pages/bookings/details/BookingResourceSelector';
import BookingResourceInfo from 'ui/_pages/bookings/details/BookingResourceInfo';
import BookingVisitorsForm from 'ui/_pages/bookings/details/BookingVisitorsForm';
import BookingNotesForm from 'ui/_pages/bookings/details/BookingNotesForm';
import BookingZoomForm from 'ui/_pages/bookings/details/BookingZoomForm';

import BookingDateTimeForm from 'ui/_pages/bookings/details/BookingDateTimeForm';
import BookingServicesForm from 'ui/_pages/bookings/details/BookingServicesForm';
import BookingDiscountForm from 'ui/_pages/bookings/details/BookingDiscountFormBW';
import BookingCancellationStatus from 'ui/_pages/bookings/details/BookingCancellationStatusBW';
import BookingStatus from 'ui/_pages/bookings/details/BookingStatus';
import BookingFormToolBar from 'ui/_pages/bookings/details/BookingFormToolBarBW';
import { CancelBookingDialog, useCancelBooking } from '@bw-ui/booking';
import { slugify } from 'env/utils/strings';
import { analyticsService } from 'env/analytics/analyticsService';
import { isResourceRltRestricted } from 'env/utils/rltGate';

type BookingDetailsModalProps = WithTranslation & {
  componentName?: string;
  close: () => void;
  appStore: any;
  bookingsStore: any;
  userStore?: any;
  isEditingBasket?: boolean;
};

const BookingDetailsModal: React.FC<BookingDetailsModalProps> = ({
  t,
  appStore,
  bookingsStore,
  close,
  isEditingBasket,
}) => {
  const {
    booking,
    setOriginalBooking,
    hasLoadedBookingPrice,
    bookingPrice,
    hasLoadedResources,
    resetBookingChangeTracking,
    trackChange,
    trackedProps,
  } = bookingsStore;

  const [isModalOpen, setIsModalOpen] = useState(Boolean(booking));

  const resource =
    booking?.ResourceId && bookingsStore.resourceMap
      ? (bookingsStore.resourceMap[booking.ResourceId] ?? null)
      : null;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      Modal.setAppElement('#react-modal');
    }
  }, []);

  const postViewItemAnalytics = useCallback(() => {
    if (!booking) return;
    analyticsService.viewItem({
      items: [
        {
          item_id: booking.ResourceId,
          item_name: booking.ResourceName,
          item_category: 'booking',
          price: booking.BookingPrice,
        },
      ],
    });
  }, [booking]);

  const loadResourceProducts = useCallback(
    (bookingData: any) =>
      bookingsStore.loadResourceProducts({
        bookingId: bookingData.Id,
        resourceId: bookingData.ResourceId,
      }),
    [bookingsStore],
  );

  const loadBookingPrice = useCallback(() => {
    bookingsStore.loadBookingPrice();
  }, [bookingsStore]);

  const resetBookingPrice = useCallback(() => {
    bookingsStore.resetBookingPrice();
  }, [bookingsStore]);

  const showBookingModal = () => {
    setIsModalOpen(true);
  };

  const hideBookingModal = useCallback(() => {
    setIsModalOpen(false);
    if (typeof window !== 'undefined' && window.location) {
      window.location.hash = '';
    }
  }, []);

  const closeHandler = useCallback(() => {
    close();
    resetBookingChangeTracking();
    hideBookingModal();
  }, [close, hideBookingModal, resetBookingChangeTracking]);

  const updateBooking = useCallback(
    // False trigger - the action() is still an inline function expression
    // eslint-disable-next-line react-hooks/use-memo
    action((prop: string, value: any) => {
      const { configuration } = appStore;
      const showEndDate = configuration['Bookings.ShowEndDate'];
      const currentBooking = bookingsStore.booking;

      if (!currentBooking) return;

      if (trackedProps.includes(prop)) trackChange(prop, value);

      const prevToTime = moment(currentBooking.ToTime);
      const prevFromTime = moment(currentBooking.FromTime);
      const prevMinutesLength = Math.abs(
        prevToTime.diff(prevFromTime, 'minutes'),
      );

      currentBooking[prop] = value;

      const toTime = moment(currentBooking.ToTime);
      const fromTime = moment(currentBooking.FromTime);

      if (prop === 'FromTime') {
        if (!showEndDate || new Date(currentBooking.ToTime) < value) {
          let newToTime = fromTime.add(prevMinutesLength, 'minutes');

          const endOfDay = moment(value).set({
            hour: 23,
            minute: 0,
          });
          if (newToTime.isAfter(endOfDay)) {
            newToTime = endOfDay;
          }
          currentBooking.ToTime = newToTime.toDate();
        }
      }

      if (prop === 'ToTime') {
        if (new Date(currentBooking.FromTime) > value) {
          let newFromTime = toTime
            .subtract(prevMinutesLength, 'minutes')
            .toDate();

          if (moment(newFromTime).isBefore(moment(value), 'day')) {
            newFromTime = moment(value).startOf('day').toDate();
          }
          currentBooking.FromTime = newFromTime;
        }
      }

      loadBookingPrice();
      postViewItemAnalytics();
    }),
    [
      appStore,
      bookingsStore,
      loadBookingPrice,
      postViewItemAnalytics,
      trackChange,
      trackedProps,
    ],
  );

  useEffect(() => {
    setIsModalOpen(Boolean(booking));
  }, [booking]);

  useEffect(() => {
    if (!isModalOpen) return;
    const win = typeof window !== 'undefined' ? (window as any) : null;
    if (win?.parentIFrame) {
      win.parentIFrame.scrollToOffset(0, -100);
    }
  }, [isModalOpen]);

  useEffect(() => {
    return () => bookingsStore.closeNewBooking();
  }, [bookingsStore]);

  useEffect(() => {
    if (booking?.ResourceId && resource) {
      resetBookingPrice();
      loadResourceProducts(booking);
      loadBookingPrice();
      postViewItemAnalytics();
    } else {
      bookingsStore.loadResources();
    }
  }, [
    booking,
    bookingsStore,
    loadBookingPrice,
    loadResourceProducts,
    postViewItemAnalytics,
    resetBookingPrice,
    resource,
  ]);

  const cancelBooking = useCancelBooking({
    close: closeHandler,
    showBookingModal,
    hideBookingModal,
  });

  const hash =
    typeof window !== 'undefined' &&
    window.location.hash &&
    window.location.hash.length > 0
      ? window.location.hash
      : null;

  const isCorporateDashboard = appStore.corporateDashboard;
  const hasLoadedData = Boolean(booking && hasLoadedResources);
  useEffect(() => {
    if (hasLoadedData) setOriginalBooking();
    if (hasLoadedData && !resource) {
      appStore.setPopMessage(
        'We are currently unable to display the full details for this booking. Please contact the building reception team for assistance.',
      );
      closeHandler();
    }
  }, [appStore, closeHandler, hasLoadedData, resource, setOriginalBooking]);

  const modalClassName = `modal-dialog modal-xl resource-id-${
    resource?.Id
  } resource-type-${slugify(
    resource?.ResourceTypeName,
  )} resource-group-${slugify(resource?.GroupName)}`;

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      overflow: 'auto',
      zIndex: 10000,
    },
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeHandler}
        shouldCloseOnEsc
        shouldCloseOnOverlayClick
        overlayClassName="booking-modal-overlay"
        className={modalClassName}
        ariaHideApp
        contentLabel={t('Booking details')}
        style={modalStyles}
      >
        <div className="d-flex flex-column align-items-end bw-fullscreen-modal tw:pointer-events-auto">
          <button
            className="close tw:text-white tw:text-4xl tw:outline-none tw:not-hover:opacity-100 tw:mt-[-3rem] tw:sm:mt-0"
            onClick={closeHandler}
          >
            <i className="icon-close tw:text-white tw:text-4xl tw:outline-none "></i>
          </button>
          <div className="bg-white tw:p-2 tw:sm:p-8 tw:z-[10001]">
            <div className="container-fluid">
              {!hasLoadedData && <LoadingSpinner />}
              {hasLoadedData && resource && (
                <form>
                  <div className="row mb-8">
                    <div className="col-lg-7">
                      {!hash && (
                        <BookingResourceSelector
                          updateBooking={updateBooking}
                          loadResourceProducts={loadResourceProducts}
                        />
                      )}

                      {!hash && (
                        <BookingResourceInfo updateBooking={updateBooking} />
                      )}

                      {(!hash || hash == '#visitors') && (
                        <BookingVisitorsForm updateBooking={updateBooking} />
                      )}

                      {(!hash || hash == '#notes') && (
                        <BookingNotesForm updateBooking={updateBooking} />
                      )}

                      {(!hash || hash == '#zoom') && (
                        <BookingZoomForm updateBooking={updateBooking} />
                      )}
                    </div>
                    <div className="col-lg-5 mt-24 mt-lg-0">
                      {!hash && (
                        <div className="bg-gray-100 p-24 rounded">
                          <BookingDateTimeForm
                            updateBooking={updateBooking}
                            resource={resource}
                          />
                          <BookingServicesForm
                            loadBookingPrice={loadBookingPrice}
                          />
                          {!isCorporateDashboard && (
                            <BookingDiscountForm
                              updateBooking={updateBooking}
                            />
                          )}
                          {hasLoadedBookingPrice &&
                            bookingPrice?.IsAvailable &&
                            !resource.RequiresConfirmation &&
                            !isResourceRltRestricted(booking?.ResourceId) && (
                              <BookingCancellationStatus />
                            )}
                          {resource?.RequiresConfirmation && (
                            <div className="alert alert-info mt-24 mb-0 booking-cancellation-status">
                              <p className="m-0 fw-500 lh-13 fs-14">
                                {t(
                                  'Please note that this resource requires approval from our team. We will review your request and get back to you within 24-48 business hours.',
                                )}
                              </p>
                            </div>
                          )}
                          {booking.ResourceId > 0 && (
                            <>
                              <BookingStatus />
                              {hasLoadedBookingPrice &&
                                bookingPrice?.IsAvailable && (
                                  <BookingFormToolBar
                                    showBookingModal={showBookingModal}
                                    hideBookingModal={hideBookingModal}
                                    updateBooking={updateBooking}
                                    isEditingBasket={isEditingBasket}
                                    close={closeHandler}
                                    onCancelBooking={
                                      cancelBooking.openCancelDialog
                                    }
                                  />
                                )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </Modal>
      {cancelBooking.snapshot && (
        <CancelBookingDialog
          open={cancelBooking.showCancelDialog}
          booking={{
            resourceName: cancelBooking.snapshot.resourceName,
            fromTime: cancelBooking.snapshot.fromTime,
            toTime: cancelBooking.snapshot.toTime,
            resourceImageUrl: cancelBooking.snapshot.resourceImageUrl,
          }}
          cancellationFee={cancelBooking.cancellationFee}
          currencyCode={cancelBooking.currencyCode}
          isLoadingFee={cancelBooking.isLoadingFee}
          isDeleting={cancelBooking.isDeleting}
          isComplete={cancelBooking.isComplete}
          onKeepBooking={cancelBooking.keepBooking}
          onCancelBooking={cancelBooking.confirmCancel}
          onClose={cancelBooking.closeAfterCancel}
        />
      )}
    </>
  );
};

export default withCustomComponent('BookingDetailsModal')(
  withTranslation()(
    inject(
      'appStore',
      'bookingsStore',
      'userStore',
    )(observer(BookingDetailsModal)),
  ),
);
